// src/services/notifications/toastDedupe.ts
/**
 * The interlock between the two channels that can toast the same arrival while
 * the app is focused: the rtc inbox socket (`useInboxSocket`) and FCM's
 * foreground handler (`useForegroundMessages`). Either can land first, so the
 * rule is first-come: whichever channel claims a notification raises the toast,
 * and the other stays quiet for a few seconds.
 *
 * This replaces an earlier `isInboxSocketConnected()` guard in the FCM handler.
 * That guard treated an **open** socket as proof the socket would **deliver**,
 * but the socket is a best-effort invalidation channel with no delivery
 * guarantee — its own header in `inboxSocket.ts` says so. Any push the socket
 * did not carry (a kind that writes no inbox row, a frame dropped over a flaky
 * mobile link, rtc lagging behind the push) was therefore swallowed in a
 * focused tab: FCM stayed silent waiting for a socket event that never came,
 * and the user saw nothing at all. Connected is not the same as delivered.
 *
 * Keyed on `notification_id` when both sides have it — the socket frame always
 * does, and the FCM `data` block may (`notification_id`). Two different
 * notifications that share a title (two deposits) then both toast. The title
 * is the fallback only when one side has no id: there is nothing else to tell
 * "the same arrival twice" from "two arrivals", and collapsing is the smaller
 * failure than toasting every arrival twice.
 */

/** Long enough to cover socket/push skew, short enough not to eat a real repeat. */
const CLAIM_WINDOW_MS = 10_000;

const idClaims = new Map<string, number>();
const titleClaims = new Map<string, { at: number; id?: string }>();

/**
 * Claims the right to toast one arrival. Returns true for the first caller
 * inside the window and false for any other channel that follows it.
 *
 * With neither an id nor a title there is no key to de-duplicate on, and it
 * always returns true: a double toast is a much smaller failure than a silent
 * one.
 */
export function claimNotificationToast({
  id,
  title
}: {
  id?: string | null;
  title?: string | null;
}): boolean {
  if (!id && !title) return true;

  const now = Date.now();
  idClaims.forEach((claimedAt, key) => {
    if (now - claimedAt > CLAIM_WINDOW_MS) idClaims.delete(key);
  });
  titleClaims.forEach((claim, key) => {
    if (now - claim.at > CLAIM_WINDOW_MS) titleClaims.delete(key);
  });

  if (id && idClaims.has(id)) return false;
  if (title) {
    const claimed = titleClaims.get(title);
    // Both carry ids and they differ: two notifications, not one twice.
    if (claimed && !(id && claimed.id && claimed.id !== id)) return false;
  }

  if (id) idClaims.set(id, now);
  if (title) titleClaims.set(title, { at: now, id: id ?? undefined });
  return true;
}
